import { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Collapse,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import RadarIcon from "@mui/icons-material/Radar";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { SlEnergy } from "react-icons/sl";
import { LuGauge } from "react-icons/lu";
import { GoPulse } from "react-icons/go";
import MovingIcon from "@mui/icons-material/Moving";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";

import {
  CenterStrategicData,
  GenerationType,
  TacticalInterval,
  EffectivenessClusterData,
  EffectivenessPowerPlantData,
} from "../../types";
import KPIComponent, { KPIComponentProps } from "./KPIComponent";
import EmptyStateNotFound from "../../../clients/components/EmptyState";
import { Screen } from "../../../../main/components/screen/Screen";
import Loading from "../../../../main/components/Loading";
import {
  formatNumberPercent,
} from "../../../dashboard/helpers/format-number";
import BaseButton from "../../../../main/components/BaseButton";
import PrimaryBaseButton from "../../../../main/components/PrimaryBaseButton";
import TacticalFiltersModal from "./TacticalFiltersModal";
import { operationsApi } from "../../../../services/api";
import StrategicClusterYtdComposedChart from "./StrategicClusterYtdComposedChart";
import { useClientClusterUsinaFilterContext } from "../../../../main/context/ClientClusterUsinaFilterContextProvider";
import { MainContext } from "../../../../main/context/MainContextProvider";
import AnaliseCausaRaizCascataChart from "./AnaliseCausaRaizCascataChart";

type StrategicYtdEntity = (EffectivenessClusterData | EffectivenessPowerPlantData) & {
  title: string;
  key: string;
};

function buildStrategicYtdEntities(
  data: CenterStrategicData,
): StrategicYtdEntity[] {
  const effCard = data.effectivenessAnalysisCard;

  return effCard.clusterData.flatMap((c) =>
    c.powerPlantData.map((p) => ({
      ...p,
      title: p.usinaName,
      key: `plant-${p.usinaId}`,
    }))
  );
}

type OperationalComment = {
  id: string;
  usinaId: string;
  usinaName: string;
  title: string;
  subtitle?: string;
  content: string;
  date: string;
};

export default function StrategicScreen() {
  const { theme, isMobile } = useContext(MainContext);
  const {
    selectedClientFilter,
    selectedCluster,
    selectedUsinaFilter,
    availableUsinas,
  } = useClientClusterUsinaFilterContext();

  const [centerData, setCenterData] = useState<CenterStrategicData | null>(null);
  const [loading, setLoading] = useState(false);

  const [selectedAtivo, setSelectedAtivo] = useState<GenerationType | undefined>(
    undefined,
  );
  const [selectedInterval, setSelectedInterval] = useState<TacticalInterval>(
    TacticalInterval.Daily,
  );
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState<Dayjs | null>(
    dayjs().subtract(7, "days"),
  );
  const [selectedEndDate, setSelectedEndDate] = useState<Dayjs | null>(dayjs());
  const [selectedWeeklyDate, setSelectedWeeklyDate] = useState<Dayjs | null>(
    dayjs(),
  );
  const [selectedMonthlyDate, setSelectedMonthlyDate] = useState<Dayjs | null>(
    dayjs(),
  );

  const [comments, setComments] = useState<OperationalComment[]>([]);
  const [commentFilterUsina, setCommentFilterUsina] = useState<string>("all");
  const [newCommentUsinaId, setNewCommentUsinaId] = useState("");
  const [newCommentTitle, setNewCommentTitle] = useState("");
  const [newCommentContent, setNewCommentContent] = useState("");
  const [usinasChartsExpanded, setUsinasChartsExpanded] = useState(false);



  const mount = async () => {
    try {
      setLoading(true);

      let startDateParam: string | undefined;
      let endDateParam: string | undefined;
      let weeklyDateParam: string | undefined;
      let monthParam: number | undefined;
      let yearParam: number | undefined;

      if (selectedInterval === TacticalInterval.Custom) {
        startDateParam = selectedStartDate?.format("YYYY-MM-DD");
        endDateParam = selectedEndDate?.format("YYYY-MM-DD");
      } else if (selectedInterval === TacticalInterval.Weekly) {
        weeklyDateParam = selectedWeeklyDate?.format("YYYY-MM-DD");
      } else if (selectedInterval === TacticalInterval.Monthly) {
        monthParam = selectedMonthlyDate?.month() ?? 0;
        yearParam = selectedMonthlyDate?.year();
      }

      const currentUsina = availableUsinas.find(
        (usina) => usina.usinaIds.id === selectedUsinaFilter?.id,
      );

      const response = await operationsApi.getCenterStrategic(
        selectedInterval,
        selectedClientFilter?.id,
        selectedCluster?.id,
        currentUsina?.usinaIds.ropInputId,
        selectedAtivo,
        startDateParam,
        endDateParam,
        weeklyDateParam,
        monthParam,
        yearParam,
      );

      setCenterData(response);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    mount();
  }, [
    selectedInterval,
    selectedAtivo,
    selectedStartDate,
    selectedEndDate,
    selectedWeeklyDate,
    selectedMonthlyDate,
    selectedClientFilter,
    selectedCluster,
    selectedUsinaFilter,
  ]);

  useEffect(() => {
    if (availableUsinas.length === 0) {
      return;
    }
    const preferred =
      availableUsinas.find((u) => u.usinaIds.id === selectedUsinaFilter?.id) ??
      availableUsinas[0];
    setNewCommentUsinaId(String(preferred.usinaIds.id));
  }, [availableUsinas, selectedUsinaFilter?.id]);

  const chartEntities = useMemo(() => {
    if (!centerData) {
      return [];
    }
    return buildStrategicYtdEntities(
      centerData,
    );
  }, [centerData, selectedCluster, selectedUsinaFilter]);

  const overallEfficacyChart = useMemo(() => {
    if (!centerData) {
      return null;
    }
    return centerData.effectivenessAnalysisCard;
  }, [centerData]);

  const spotlightEntities = useMemo(() => chartEntities.slice(0, 2), [chartEntities]);

  const filteredComments = useMemo(() => {
    if (commentFilterUsina === "all") {
      return comments;
    }
    return comments.filter((c) => c.usinaId === commentFilterUsina);
  }, [comments, commentFilterUsina]);

  const headerDateLabel = dayjs()
    .locale("pt-br")
    .format("DD [de] MMMM, YYYY [-] HH:mm");

  const averagePower = centerData?.averagePowerCard.overallAverage ?? 0;
  const generatedEnergy = centerData?.generatedEnergyCard.overallTotal ?? 0;
  const manageableLoss = centerData?.manageableLossCard.overallTotal ?? 0;
  const generationEffectivenesValue = centerData?.generationEffectivenessCard.overallAverage
    ? centerData.generationEffectivenessCard.overallAverage / 100
    : 0;

  const averageExpectedPR = centerData?.performanceRatioCard?.expectedPerformanceRatio ?? 0;
  const currentPR = centerData?.performanceRatioCard.overallAverage ?? 0;
  const prDeviationPercent = averageExpectedPR > 0 ? ((currentPR - averageExpectedPR) / averageExpectedPR) * 100 : 0;

  const cardsData: KPIComponentProps[] = [
    {
      icon: <LuGauge style={{ color: "#528AD1" }} />,
      sufixx: "%",
      title: "Performance Ratio",
      value: formatNumberPercent(centerData?.performanceRatioCard.overallAverage ?? 0, 2, false, false) || "0%",
      gridSize: { xs: 6, md: 2 },
      children:
        <Box sx={{ display: "flex", flexDirection: "row" }}>
          {prDeviationPercent > 0 ? <MovingIcon style={{ color: "#47D27B", fontSize: "18px", marginRight: "8px" }} /> : <TrendingDownIcon style={{ color: "#EF4444", fontSize: "18px", marginRight: "8px" }} />}
          <Typography sx={{ fontSize: "13px", fontWeight: "400", color: prDeviationPercent >= 0 ? "#47D27B" : "#EF4444" }}>
            {prDeviationPercent >= 0 ? "+" : ""}{prDeviationPercent.toFixed(1)}%
          </Typography>
        </Box>
    },
    {
      icon: <RadarIcon sx={{ color: "#B980F3" }} />,
      sufixx: "kW",
      title: "Potência Média",
      gridSize: { xs: 6, md: 2 },
      value: averagePower.toFixed(2) || "0%",
      children:
        <Box sx={{ display: "flex", flexDirection: "row" }}>
          <Typography sx={{ fontSize: "13px", fontWeight: "400", color: "#47D27B" }}>Injetada na rede</Typography>
        </Box>
    },
    {
      icon: <SlEnergy style={{ color: "#FBBF24" }} />,
      sufixx: "",
      title: "Energia Gerada",
      value: generatedEnergy.toFixed(2) || "0.00",
      gridSize: { xs: 6, md: 2 },
      children:
        <Box sx={{ display: "flex", flexDirection: "row" }}>
          <Typography sx={{ fontSize: "13px", fontWeight: "400", color: "#F85F56" }}>MWh no período</Typography>
        </Box>
    },
    {
      icon: <GoPulse style={{ color: "#2CCBB8" }} />,
      sufixx: "%",
      title: "Eficácia de Geração",
      value: formatNumberPercent(generationEffectivenesValue, 2, false) || "0%",
      gridSize: { xs: 6, md: 2 },
      children:
        <Box sx={{ display: "flex", flexDirection: "row" }}>
          <Typography sx={{ fontSize: "13px", fontWeight: "400", color: "#47D27B" }}>Eficiência operacional</Typography>
        </Box>
    },
    {
      icon: <TrendingDownIcon sx={{ color: "#FB773B" }} />,
      sufixx: "",
      title: "Perdas Gerenciáveis",
      value: manageableLoss.toFixed(2) || "0.00",
      gridSize: { xs: 6, md: 2 },
      children:
        <Box sx={{ display: "flex", flexDirection: "row" }}>
          <Typography sx={{ fontSize: "13px", fontWeight: "400" }}>MWh no período</Typography>
        </Box>
    },
    {
      icon: <AccessTimeOutlinedIcon sx={{ color: "#47D27B" }} />,
      sufixx: "%",
      title: "Disponibilidade",
      value: formatNumberPercent(centerData?.availabilityCard.overallAverage ?? 0, 2, false, false) || "0%",
      gridSize: { xs: 6, md: 2 },
      children:
        <Box sx={{ display: "flex", flexDirection: "row" }}>
          <Typography sx={{ fontSize: "13px", fontWeight: "400", color: "#47D27B" }}>Média dos inversores</Typography>
        </Box>
    },

  ]

  const handleAddComment = () => {
    const title = newCommentTitle.trim();
    const content = newCommentContent.trim();
    if (!title || !content || !newCommentUsinaId) {
      return;
    }
    const usina = availableUsinas.find(
      (u) => String(u.usinaIds.id) === newCommentUsinaId,
    );
    const usinaName = usina?.name ?? "Usina";
    const id =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `c-${Date.now()}`;
    setComments((prev) => [
      {
        id,
        usinaId: newCommentUsinaId,
        usinaName,
        title,
        subtitle: "Operacional",
        content,
        date: dayjs().locale("pt-br").format("DD/MM/YYYY HH:mm"),
      },
      ...prev,
    ]);
    setNewCommentTitle("");
    setNewCommentContent("");
  };

  if (loading) {
    return (
      <Screen.Container>
        <Loading />
      </Screen.Container>
    );
  }

  if (!centerData) {
    return (
      <Screen.Container>
        <EmptyStateNotFound title="Nenhum dado encontrado" />
      </Screen.Container>
    );
  }

  return (
    <>
      <Screen.Container sx={{ paddingLeft: "10px", paddingRight: "10px" }}>
        <Grid
          container
          paddingX={{ xs: "5px", sm: "30px" }}
          sx={{
            marginY: "14px",
            alignItems: "flex-start",
          }}
        >
          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 1.5,
              alignItems: "flex-start",
              width: "100%",
            }}
          >
            <Box
              sx={{
                flex: { xs: "none", md: "11 1 0" },
                minWidth: 0,
                width: { xs: "100%", md: "auto" },
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-start",
                gap: 1.5,
                textAlign: "left",
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "8px",
                  backgroundColor:
                    theme.muiTheme?.palette?.primary?.main ?? "#2CCBB8",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <InsightsOutlinedIcon sx={{ color: "#fff", fontSize: 22 }} />
              </Box>
              <Box sx={{ minWidth: 0, textAlign: "left" }}>
                <Typography sx={{ fontSize: "20px", fontWeight: 600, textAlign: "left" }}>
                  Dashboard estratégico
                </Typography>
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 400,
                    color: theme.secondaryTextColor || "#8F969E",
                    marginTop: "4px",
                    textAlign: "left",
                  }}
                >
                  Gestão operacional com apuração automatizada e análise comparativa.
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                flex: { xs: "none", md: "9 1 0" },
                minWidth: 0,
                width: { xs: "100%", md: "auto" },
                display: "flex",
                flexDirection: "column",
                alignItems: { xs: "flex-start", sm: "flex-end" },
                gap: 1,
                flexShrink: 0,
                marginTop: { xs: 0, md: 0 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: theme.secondaryTextColor || "#8F969E",
                  justifyContent: { xs: "flex-start", sm: "flex-end" },
                  width: "100%",
                }}
              >
                <CalendarTodayIcon sx={{ fontSize: 20, flexShrink: 0 }} />
                <Typography sx={{ fontSize: "13px", textAlign: { xs: "left", sm: "right" } }}>
                  {headerDateLabel}
                </Typography>
              </Box>
              <BaseButton
                sx={{ width: { xs: "100%", sm: "auto" }, height: "35px", alignSelf: { xs: "stretch", sm: "flex-end" } }}
                startIcon={<FilterAltIcon style={{ fontSize: "20px" }} />}
                title="Filtros do período"
                onClick={() => setShowFiltersModal(true)}
              />
            </Box>
          </Grid>
        </Grid>

        <Grid
          container
          sx={{
            paddingLeft: "6px",
            paddingRight: "2px",
            marginY: "14px",
            rowGap: "12px",
          }}
        >
          {cardsData.map((item, index) => (
            <KPIComponent key={item.title + String(index)} {...item} />
          ))}
        </Grid>

        <Grid container spacing={1.5} sx={{ marginBottom: "12px", alignItems: "flex-start" }}>
          <Grid item xs={12}>
            <Typography
              component="h2"
              sx={{
                fontSize: { xs: "15px", sm: "17px" },
                fontWeight: 600,
                textAlign: "left",
                marginBottom: 0,
              }}
            >
              Eficácia de Geração (%) e Geração (MWm) — Visão YTD
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", lg: "row" },
              gap: 1.5,
              alignItems: "flex-start",
              width: "100%",
            }}
          >
            <Box
              sx={{
                flex: { xs: "none", lg: "11 1 0" },
                minWidth: 0,
                width: { xs: "100%", lg: "auto" },
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0,
                  width: "100%",
                  backgroundColor: theme.cardBoxBackgroudColor,
                  borderRadius: "8px",
                  padding: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 1.5,
                    marginBottom: 2,
                    textAlign: "left",
                  }}
                >
                  <ShowChartIcon sx={{ color: "#528AD1", fontSize: 28, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: "16px", fontWeight: 600, textAlign: "left" }}>
                    Eficácia de Geração
                  </Typography>
                </Box>
                {!overallEfficacyChart && spotlightEntities.length === 0 ? (
                  <Typography sx={{ fontSize: "14px", color: theme.secondaryTextColor }}>
                    Não há séries mensais para o recorte de filtros selecionado.
                  </Typography>
                ) : (
                  <>
                    {overallEfficacyChart ? (
                      <Box
                        sx={{
                          marginBottom:
                            spotlightEntities.length > 0 ? 0 : 2,
                        }}
                      >
                        <StrategicClusterYtdComposedChart
                          title="Média Geral"
                          data={overallEfficacyChart}
                          variant="embedded"
                        />
                      </Box>
                    ) : null}
                    {overallEfficacyChart &&
                      spotlightEntities.length > 0 ? (
                      <Divider
                        sx={{
                          my: 2,
                          borderColor:
                            theme.cardBorderColor || "rgba(255,255,255,0.12)",
                        }}
                      />
                    ) : null}
                    {spotlightEntities.length > 0 ? (
                      <Box
                        sx={{
                          marginTop: overallEfficacyChart ? 0 : 2,
                        }}
                      >
                        <Box
                          role="button"
                          tabIndex={0}
                          aria-expanded={usinasChartsExpanded}
                          aria-label={
                            usinasChartsExpanded
                              ? "Recolher gráficos por usina"
                              : "Expandir gráficos por usina"
                          }
                          onClick={() => setUsinasChartsExpanded((e) => !e)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setUsinasChartsExpanded((x) => !x);
                            }
                          }}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                            cursor: "pointer",
                            color: "inherit",
                            textAlign: "left",
                            borderRadius: 1,
                            paddingY: 0.5,
                            "&:focus-visible": { outline: `2px solid ${theme.primaryTextColor || "#fff"}`, outlineOffset: 2 },
                          }}
                        >
                          <Typography sx={{ fontSize: "15px", fontWeight: 600 }}>
                            Gráficos por usina
                          </Typography>
                          <ExpandMoreIcon
                            sx={{
                              transform: usinasChartsExpanded ? "rotate(180deg)" : "none",
                              transition: "transform 0.2s",
                              color: theme.secondaryTextColor,
                            }}
                          />
                        </Box>
                        <Collapse in={usinasChartsExpanded}>
                          <Grid container spacing={2} sx={{ marginTop: 1 }}>
                            {spotlightEntities.map((entity) => (
                              <Grid item xs={12} md={12} key={`eff-${entity.key}`}>
                                <StrategicClusterYtdComposedChart
                                  title={entity.title}
                                  data={entity}
                                  variant="embedded"
                                  compact
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </Collapse>
                      </Box>
                    ) : null}
                  </>
                )}
              </Box>
              <Box
                sx={{
                  width: "100%",
                  minWidth: 0,
                  backgroundColor: theme.cardBoxBackgroudColor,
                  borderRadius: "8px",
                  padding: 2,
                  height: isMobile ? "auto" : 680,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    marginBottom: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <ChatBubbleOutlineIcon
                    sx={{ color: theme.secondaryTextColor, fontSize: 22 }}
                  />
                  <Typography sx={{ fontSize: "15px", fontWeight: 500 }}>
                    Comentários operacionais
                  </Typography>
                </Box>

                <Grid container spacing={2} sx={{ marginBottom: 2 }}>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="comment-filter-usina">
                        Filtrar por usina
                      </InputLabel>
                      <Select
                        labelId="comment-filter-usina"
                        label="Filtrar por usina"
                        value={commentFilterUsina}
                        onChange={(e) =>
                          setCommentFilterUsina(String(e.target.value))
                        }
                      >
                        <MenuItem value="all">Todas</MenuItem>
                        {availableUsinas.map((u) => (
                          <MenuItem
                            key={String(u.usinaIds.id)}
                            value={String(u.usinaIds.id)}
                          >
                            {u.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                <Box
                  sx={{
                    flex: 1,
                    minHeight: isMobile ? 220 : 260,
                    overflowY: "auto",
                    marginBottom: 2,
                    paddingRight: 0.5,
                  }}
                >
                  {filteredComments.length === 0 ? (
                    <Typography
                      sx={{ fontSize: "13px", color: theme.secondaryTextColor }}
                    >
                      Nenhum comentário neste filtro.
                    </Typography>
                  ) : (
                    filteredComments.map((c) => (
                      <Box
                        key={c.id}
                        sx={{
                          borderBottom: `1px solid ${theme.cardBorderColor || "rgba(255,255,255,0.08)"}`,
                          paddingY: 1.5,
                        }}
                      >
                        <Typography sx={{ fontSize: "15px", fontWeight: 600 }}>
                          {c.usinaName} — {c.title}
                        </Typography>
                        {c.subtitle ? (
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: "#47D27B",
                              marginTop: 0.25,
                            }}
                          >
                            {c.subtitle}
                          </Typography>
                        ) : null}
                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: theme.secondaryTextColor,
                            marginTop: 0.75,
                            whiteSpace: "pre-wrap",
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                          }}
                        >
                          {c.content}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "11px",
                            color: theme.secondaryTextColor,
                            marginTop: 1,
                          }}
                        >
                          {c.date}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Box>

                <Typography
                  sx={{ fontSize: "13px", fontWeight: 500, marginBottom: 1 }}
                >
                  Novo comentário
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="new-comment-usina">Usina</InputLabel>
                      <Select
                        labelId="new-comment-usina"
                        label="Usina"
                        value={newCommentUsinaId}
                        onChange={(e) =>
                          setNewCommentUsinaId(String(e.target.value))
                        }
                      >
                        {availableUsinas.map((u) => (
                          <MenuItem
                            key={String(u.usinaIds.id)}
                            value={String(u.usinaIds.id)}
                          >
                            {u.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Título"
                      value={newCommentTitle}
                      onChange={(e) => setNewCommentTitle(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label="Conteúdo"
                      value={newCommentContent}
                      onChange={(e) => setNewCommentContent(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <PrimaryBaseButton title="Adicionar" onClick={handleAddComment} />
                  </Grid>
                </Grid>
              </Box>
            </Box>
            <Box
              sx={{
                flex: { xs: "none", lg: "9 1 0" },
                minWidth: 0,
                width: { xs: "100%", lg: "auto" },
                display: "flex",
                height: "441px",
                flexDirection: "column",
                gap: 2,
              }}
            >
              {centerData && (
                <AnaliseCausaRaizCascataChart
                  data={centerData.rootCauseAnalysisCard.overallTotal}
                  height={"100%"}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Screen.Container>

      <TacticalFiltersModal
        open={showFiltersModal}
        setOpen={setShowFiltersModal}
        selectedAtivo={selectedAtivo}
        setSelectedAtivo={setSelectedAtivo}
        selectedInterval={selectedInterval}
        setSelectedInterval={setSelectedInterval}
        selectedStartDate={selectedStartDate}
        setSelectedStartDate={setSelectedStartDate}
        selectedEndDate={selectedEndDate}
        setSelectedEndDate={setSelectedEndDate}
        selectedWeeklyDate={selectedWeeklyDate}
        setSelectedWeeklyDate={setSelectedWeeklyDate}
        selectedMonthlyDate={selectedMonthlyDate}
        setSelectedMonthlyDate={setSelectedMonthlyDate}
      />
    </>
  );
}
